import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Filter, Loader2, RefreshCw, Radar, SlidersHorizontal, ChevronDown } from 'lucide-react';
import MobileSheet from '../MobileSheet';
import { RetestTag } from './retest/RetestTag';
import RetestDetail from './retest/RetestDetail';

const TFS = ['5m', '15m', '1h', '4h', '1d'];

// LocalStorage keys for persisting scanner filter state across
// tab switches / refreshes (per-browser, per-user).
const LS_KEY = 'atde.crypto.scanner.v1';
const loadPrefs = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null'); }
  catch { return null; }
};
const savePrefs = (p) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(p)); } catch { /* noop */ }
};

/**
 * MarketScanner — Phase 53.B. Runs technical filters against the top
 * Binance USDT universe and shows the rows that match, with a click
 * to jump into the chart for that symbol.
 */
const VENUE_QUOTES = { binance: ['USDT', 'USDC', 'BTC'], coinbase: ['USD', 'USDC'] };
const VENUE_LABEL = { binance: 'Binance', coinbase: 'Coinbase' };
// the chart workspace streams Binance.US — open the same base asset there
const chartPick = (r) => ({ symbol: r.symbol.includes('-') ? `${r.base}USDT` : r.symbol, base: r.base,
  quote: r.symbol.includes('-') ? 'USDT' : r.quote });

function VenueSwitch({ venue, onChange, testId, large }) {
  return (
    <div className="inline-flex border border-[var(--border-default)]" data-testid={testId}>
      {Object.keys(VENUE_QUOTES).map((v) => (
        <button key={v} onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
                className={`${large ? 'px-4 py-2 text-[11px] min-h-[40px]' : 'px-2 py-0.5 text-[9px]'} uppercase tracking-widest transition-colors duration-150 ${venue === v ? 'bg-cyan-sys text-black font-semibold' : 'text-[var(--txt-secondary)] hover:text-cyan-sys'}`}>
          {VENUE_LABEL[v]}
        </button>
      ))}
    </div>
  );
}

export default function MarketScanner({ onPick }) {
  const [catalog, setCatalog] = useState([]);        // [{key,label}]
  const labelOf = Object.fromEntries(catalog.map((f) => [f.key, f.label]));
  const initial = loadPrefs();
  const [selected, setSelected] = useState(new Set(initial?.selected || ['above_ema200', 'rsi_oversold']));
  const [tf, setTf] = useState(initial?.tf || '1h');
  const [mode, setMode] = useState(initial?.mode || 'any');          // any | all
  const [size, setSize] = useState(initial?.size || 100);
  const [venue, setVenue] = useState(initial?.venue && VENUE_QUOTES[initial.venue] ? initial.venue : 'binance');
  const [quote, setQuote] = useState(
    VENUE_QUOTES[venue].includes(initial?.quote) ? initial.quote : VENUE_QUOTES[venue][0]);
  const [scannedVenue, setScannedVenue] = useState(null);
  const changeVenue = (v) => { setVenue(v); setQuote(VENUE_QUOTES[v][0]); };
  const [rows, setRows] = useState([]);
  const [scanned, setScanned] = useState(0);
  const [scannedTf, setScannedTf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sortKey, setSortKey] = useState('volume');
  const [sortDir, setSortDir] = useState('desc');
  // Mobile filter sheet — desktop keeps chips inline
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  // Iter 111 · expanded discovery cards survive re-scans (keyed by symbol)
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = (sym) => setExpanded((prev) => {
    const s = new Set(prev);
    if (s.has(sym)) s.delete(sym); else s.add(sym);
    return s;
  });

  // Persist any filter change so the operator doesn't lose their
  // setup when they leave the scanner tab and come back.
  useEffect(() => {
    savePrefs({ selected: [...selected], tf, mode, size, quote, venue });
  }, [selected, tf, mode, size, quote, venue]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/crypto/scanner/filters');
        setCatalog(data?.filters || []);
      } catch { /* noop */ }
    })();
  }, []);

  const run = async () => {
    if (busy) return;
    const filters = [...selected];
    if (!filters.length) { setErr('pick at least one filter'); return; }
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post('/crypto/scanner/run', {
        filters, tf, match_mode: mode, universe_size: size, quote, venue,
      });
      setRows(data?.matches || []);
      setScanned(data?.scanned || 0);
      setScannedTf(data?.tf || tf);
      setScannedVenue(data?.venue || venue);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'scan failed');
    } finally { setBusy(false); }
  };

  const toggle = (k) => {
    const s = new Set(selected);
    if (s.has(k)) s.delete(k); else s.add(k);
    setSelected(s);
  };

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const setSort = (k) => {
    if (sortKey === k) setSortDir((d) => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="crypto-scanner">
      {/* Header + controls */}
      <div className="border-b border-[var(--border-default)] bg-[var(--bg-panel)] p-3">
        <div className="flex items-center gap-2 mb-2">
          <Radar size={14} className="text-cyan-sys" />
          <div className="text-[10px] tracking-[0.35em] uppercase text-cyan-sys">// market scanner</div>
          <div className="text-[9px] text-[var(--txt-muted)] ml-2 hidden sm:block">
            top {size} · {tf} · match {mode.toUpperCase()}
          </div>
          {/* Mobile filter button — opens sheet */}
          <button
            onClick={() => setMobileFiltersOpen(true)}
            data-testid="scanner-mobile-filter-btn"
            className="md:hidden ml-auto inline-flex items-center gap-1.5 px-3 py-1 text-[10px] tracking-widest uppercase border border-cyan-sys text-cyan-sys min-h-[36px]"
          >
            <SlidersHorizontal size={11} />
            filters
            {selected.size > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[9px] px-1 bg-cyan-sys text-black rounded-full">
                {selected.size}
              </span>
            )}
          </button>
          <button
            onClick={run}
            disabled={busy}
            data-testid="scanner-run"
            className="md:ml-auto px-3 py-1 text-[10px] tracking-widest uppercase border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black disabled:opacity-40 flex items-center gap-1.5 min-h-[36px]"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
            {busy ? 'scanning…' : 'run scan'}
          </button>
        </div>

        {/* Desktop-only inline config + filter chips */}
        <div className="hidden md:block">
          {/* Config row */}
          <div className="flex items-center flex-wrap gap-3 mb-2 text-[10px]">
            <div className="flex items-center gap-1">
              <span className="text-[9px] tracking-widest text-[var(--txt-muted)]">TF</span>
              {TFS.map((x) => (
                <button
                  key={x}
                  onClick={() => setTf(x)}
                  data-testid={`scanner-tf-${x}`}
                  className={`px-2 py-0.5 border text-[9px] tracking-widest uppercase ${tf === x ? 'border-cyan-sys text-cyan-sys' : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:text-cyan-sys'}`}
                >{x}</button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[9px] tracking-widest text-[var(--txt-muted)]">MATCH</span>
              {['any', 'all'].map((m) => (
                <button key={m} onClick={() => setMode(m)}
                        data-testid={`scanner-mode-${m}`}
                        className={`px-2 py-0.5 border text-[9px] tracking-widest uppercase ${mode === m ? 'border-cyan-sys text-cyan-sys' : 'border-[var(--border-default)] text-[var(--txt-secondary)]'}`}
                >{m}</button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[9px] tracking-widest text-[var(--txt-muted)]">SIZE</span>
              <select value={size} onChange={(e) => setSize(Number(e.target.value))}
                      data-testid="scanner-size"
                      className="bg-black border border-[var(--border-default)] text-[9px] uppercase tracking-widest px-2 py-0.5 text-[var(--txt-secondary)]">
                {[50, 100, 150, 200].map((n) => <option key={n} value={n}>Top {n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[9px] tracking-widest text-[var(--txt-muted)]">EXCHANGE</span>
              <VenueSwitch venue={venue} onChange={changeVenue} testId="scanner-venue" />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[9px] tracking-widest text-[var(--txt-muted)]">QUOTE</span>
              <select value={quote} onChange={(e) => setQuote(e.target.value)}
                      data-testid="scanner-quote"
                      className="bg-black border border-[var(--border-default)] text-[9px] uppercase tracking-widest px-2 py-0.5 text-[var(--txt-secondary)]">
                {VENUE_QUOTES[venue].map((q) => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
          </div>

          {/* Filter chips */}
          <div className="flex flex-wrap gap-1">
            <Filter size={11} className="text-[var(--txt-muted)] self-center" />
            {catalog.map((f) => {
              const on = selected.has(f.key);
              return (
                <button
                  key={f.key}
                  onClick={() => toggle(f.key)}
                  data-testid={`scanner-filter-${f.key}`}
                  className={`px-2 py-0.5 border text-[9px] tracking-widest uppercase ${on ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10' : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:text-cyan-sys'}`}
                >{f.label}</button>
              );
            })}
          </div>
        </div>

        {/* Mobile config summary — condensed active state */}
        <div className="md:hidden flex items-center flex-wrap gap-2 text-[9px] tracking-widest uppercase text-[var(--txt-muted)]">
          <span>tf: <span className="text-cyan-sys">{tf}</span></span>
          <span>match: <span className="text-cyan-sys">{mode}</span></span>
          <span>size: <span className="text-cyan-sys">{size}</span></span>
          <span>quote: <span className="text-cyan-sys">{quote}</span></span>
          <span>exchange: <span className="text-cyan-sys" data-testid="scanner-venue-summary">{VENUE_LABEL[venue]}</span></span>
        </div>
      </div>

      {/* Results table */}
      <div className="flex-1 min-h-0 overflow-x-auto lg:overflow-auto">
        {err && <div className="p-3 text-[10px] text-sell">// {err}</div>}
        {!err && !rows.length && !busy && (
          <div className="p-8 text-center text-[10px] text-[var(--txt-muted)]">
            // pick your filters and press RUN SCAN
          </div>
        )}
        {rows.length > 0 && (
          <table className="w-full text-[10px] font-mono" data-testid="scanner-results-table">
            <thead className="sticky top-0 bg-black border-b border-[var(--border-default)]">
              <tr className="text-[9px] tracking-[0.3em] uppercase text-[var(--txt-muted)]">
                <th className="text-left px-3 py-1.5">Symbol</th>
                <th className="text-right px-3 py-1.5 cursor-pointer" onClick={() => setSort('price')}>Price</th>
                <th className="text-right px-3 py-1.5 cursor-pointer" onClick={() => setSort('change_pct')}>24h %</th>
                <th className="text-right px-3 py-1.5 cursor-pointer" onClick={() => setSort('volume')}>Volume</th>
                <th className="text-right px-3 py-1.5 cursor-pointer" onClick={() => setSort('rsi')}>RSI</th>
                <th className="text-right px-3 py-1.5 cursor-pointer" onClick={() => setSort('vol_ratio')}>VolX</th>
                <th className="text-left px-3 py-1.5">Matched</th>
                <th className="text-left px-3 py-1.5">Retest ({scannedTf || tf} · {VENUE_LABEL[scannedVenue || venue]})</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <React.Fragment key={r.symbol}>
                <tr
                  onClick={() => onPick?.(chartPick(r))}
                  data-testid={`scanner-row-${r.symbol}`}
                  className={`border-b border-[var(--border-default)] hover:bg-cyan-sys/5 cursor-pointer ${expanded.has(r.symbol) ? 'bg-cyan-sys/5' : ''}`}
                >
                  <td className="px-3 py-1 text-cyan-sys tabular-nums whitespace-nowrap">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleExpand(r.symbol); }}
                      data-testid={`scanner-expand-${r.symbol}`}
                      aria-expanded={expanded.has(r.symbol)}
                      aria-label={`expand ${r.symbol} retest details`}
                      className="inline-flex items-center justify-center w-6 h-6 mr-1 -ml-1 border border-transparent hover:border-cyan-sys/60 align-middle"
                    >
                      <ChevronDown size={12} className={`transition-transform duration-200 ${expanded.has(r.symbol) ? 'rotate-180' : ''}`} />
                    </button>
                    {r.base}/{r.quote}
                  </td>
                  <td className="px-3 py-1 text-right tabular-nums">${Number(r.price).toFixed(4)}</td>
                  <td className={`px-3 py-1 text-right tabular-nums ${(r.change_pct ?? 0) >= 0 ? 'text-buy' : 'text-sell'}`}>
                    {(r.change_pct ?? 0).toFixed(2)}%
                  </td>
                  <td className="px-3 py-1 text-right tabular-nums text-[var(--txt-secondary)]">
                    {r.volume >= 1e9 ? `$${(r.volume/1e9).toFixed(2)}B`
                      : r.volume >= 1e6 ? `$${(r.volume/1e6).toFixed(2)}M`
                      : r.volume >= 1e3 ? `$${(r.volume/1e3).toFixed(1)}K`
                      : `$${(r.volume ?? 0).toFixed(0)}`}
                  </td>
                  <td className={`px-3 py-1 text-right tabular-nums ${r.rsi != null && r.rsi < 30 ? 'text-buy' : r.rsi != null && r.rsi > 70 ? 'text-sell' : 'text-[var(--txt-secondary)]'}`}>
                    {r.rsi ?? '—'}
                  </td>
                  <td className="px-3 py-1 text-right tabular-nums text-[var(--txt-secondary)]">
                    {r.vol_ratio ?? '—'}
                  </td>
                  <td className="px-3 py-1">
                    <div className="flex flex-wrap gap-1">
                      {r.matched.map((m) => (
                        <span key={m}
                              className="text-[8px] tracking-widest uppercase px-1 py-0.5 border border-cyan-sys/60 text-cyan-sys">
                          {labelOf[m] || m.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-1" data-testid={`scanner-retest-cell-${r.symbol}`}>
                    <div className="flex flex-wrap gap-1">
                      {r.retest?.data_status && r.retest.data_status !== 'OK' && (
                        <span className="text-[8px] tracking-widest uppercase text-sell">{r.retest.data_status.replace(/_/g, ' ')}</span>
                      )}
                      {(r.retest?.tags || []).map((t, i) => (
                        <RetestTag key={t.label} tag={t} testId={`scanner-retest-tag-${r.symbol}-${i}`} />
                      ))}
                    </div>
                  </td>
                </tr>
                {expanded.has(r.symbol) && (
                  <tr data-testid={`scanner-expanded-${r.symbol}`} className="border-b border-[var(--border-default)]">
                    <td colSpan={8} className="p-0">
                      <div className="sticky left-0 max-w-[100vw] md:max-w-none">
                        <RetestDetail row={r} initialTf={scannedTf || tf} venue={scannedVenue || venue} labels={labelOf} />
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-[var(--border-default)] px-3 py-1 text-[9px] tracking-widest uppercase text-[var(--txt-muted)] flex justify-between">
        <span>scanned {scanned} · matched {rows.length}</span>
        <span>{selected.size} filter{selected.size === 1 ? '' : 's'} active</span>
      </div>

      {/* Mobile filter sheet */}
      <MobileSheet
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        title="// Scanner Filters"
        testId="scanner-mobile-filter-sheet"
      >
        <div className="flex flex-col gap-5">
          {/* Timeframe */}
          <div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[var(--txt-muted)] mb-2">timeframe</div>
            <div className="flex flex-wrap gap-2">
              {TFS.map((x) => (
                <button
                  key={x}
                  onClick={() => setTf(x)}
                  data-testid={`scanner-tf-sheet-${x}`}
                  className={`px-3 py-2 border text-[10px] tracking-widest uppercase min-h-[40px] ${tf === x ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10' : 'border-[var(--border-default)] text-[var(--txt-secondary)]'}`}
                >{x}</button>
              ))}
            </div>
          </div>

          {/* Match mode */}
          <div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[var(--txt-muted)] mb-2">match mode</div>
            <div className="flex gap-2">
              {['any', 'all'].map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  data-testid={`scanner-mode-sheet-${m}`}
                  className={`px-4 py-2 border text-[10px] tracking-widest uppercase min-h-[40px] ${mode === m ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10' : 'border-[var(--border-default)] text-[var(--txt-secondary)]'}`}
                >{m}</button>
              ))}
            </div>
          </div>

          {/* Size + Quote */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-[var(--txt-muted)] mb-2">universe size</div>
              <select value={size} onChange={(e) => setSize(Number(e.target.value))}
                      data-testid="scanner-size-sheet"
                      className="w-full bg-black border border-[var(--border-default)] text-[12px] uppercase tracking-widest px-3 py-2 text-[var(--txt-secondary)] min-h-[40px]">
                {[50, 100, 150, 200].map((n) => <option key={n} value={n}>Top {n}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-[var(--txt-muted)] mb-2">quote</div>
              <select value={quote} onChange={(e) => setQuote(e.target.value)}
                      data-testid="scanner-quote-sheet"
                      className="w-full bg-black border border-[var(--border-default)] text-[12px] uppercase tracking-widest px-3 py-2 text-[var(--txt-secondary)] min-h-[40px]">
                {VENUE_QUOTES[venue].map((q) => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <div className="text-[9px] tracking-[0.3em] uppercase text-[var(--txt-muted)] mb-2">exchange</div>
              <VenueSwitch venue={venue} onChange={changeVenue} testId="scanner-venue-sheet" large />
            </div>
          </div>

          {/* Filter chips */}
          <div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[var(--txt-muted)] mb-2">filters ({selected.size} active)</div>
            <div className="flex flex-wrap gap-2">
              {catalog.map((f) => {
                const on = selected.has(f.key);
                return (
                  <button
                    key={f.key}
                    onClick={() => toggle(f.key)}
                    data-testid={`scanner-filter-sheet-${f.key}`}
                    className={`px-3 py-2 border text-[10px] tracking-widest uppercase min-h-[40px] ${on ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10' : 'border-[var(--border-default)] text-[var(--txt-secondary)]'}`}
                  >{f.label}</button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 sticky bottom-0 bg-[var(--bg-panel)] pt-2 -mx-1 px-1">
            <button
              onClick={() => setSelected(new Set())}
              data-testid="scanner-filter-sheet-clear"
              className="flex-1 px-3 py-3 border border-[var(--border-muted)] text-[10px] tracking-widest uppercase text-[var(--txt-secondary)] hover:border-sell hover:text-sell min-h-[44px]"
            >clear</button>
            <button
              onClick={() => { setMobileFiltersOpen(false); run(); }}
              disabled={busy || selected.size === 0}
              data-testid="scanner-filter-sheet-apply"
              className="flex-1 px-3 py-3 border border-cyan-sys text-cyan-sys bg-cyan-sys/10 text-[10px] tracking-widest uppercase disabled:opacity-40 min-h-[44px]"
            >apply &amp; scan</button>
          </div>
        </div>
      </MobileSheet>
    </div>
  );
}
